import { Col, Row, Form, Button } from 'react-bootstrap';
import { useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../utils/auth-provider";
import BasicSpinner from '../common/basic-spinner';
import RemoveButton from '../common/remove-button';
import UpdateFormTags from '../common/update-form-tags';
import { Toaster, createToast } from '../common/toasts';
import { deleteUserCollection } from '../../utils/fetch-helpers';
import { CollectionTypes } from '../../enums';

//================================================================

export default function CollectionUpdateForm({collection, onUpdate, onCancel}){

  const user = useAuth();

  const navigate = useNavigate();

  const [working, setWorking] = useState(false);
  const [toasts, setToasts] = useState([]);

  const [name, setName] = useState(collection.name);
  const [description, setDescription] = useState(collection.description);
  const [isPrivate, setIsPrivate] = useState(collection.is_private);
  const [remove_tags, setRemoveTags ] = useState([]);
  const [add_tags, setAddTags ] = useState([]);
  const [type, setType] = useState(collection.type);

  const sendUpdateRequest = useCallback(async (data, collectionId) => {
    console.log(`sendUpdateRequest() collectionId: ${collectionId}`,", data: ", data);
    if (Object.keys(data).length <= 0) return null;

    setWorking(true);

    const updatedCollection = await fetch(`/api/collection/${collectionId}`, {
      method : 'PUT',
      body : JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${user.token}`
      }
    })
    .then(async (response) => {
      const result = await response.json();
      if (response.ok) {
        onUpdate(result);
        return result;
      }
      setToasts([...toasts, createToast(
        `Failed to update collection`,
        `${response.status} - ${response.json()}`
      )]);
    })
    .catch((error) => {
      setToasts([...toasts, createToast(
        "Error",
        `While updating collection ${collectionId}: ${error.message}`
      )]);
    });

    setWorking(false);

    return updatedCollection;
  }, [onUpdate]);

  const submitHandler = useCallback(async () => {
    const data = {};

    if (name !== collection.name){
      data['name'] = name;
    }

    if (description !== collection.description){
      data['description'] = description;
    }

    if (isPrivate !== collection.is_private){
      data['is_private'] = isPrivate;
    }

    if (type !== collection.type){
      data['type'] = type;
    }

    if (add_tags.length > 0) data['add_tags'] = add_tags;
    if (remove_tags.length > 0) data['remove_tags'] = remove_tags;

    if (Object.keys(data).length > 0) await sendUpdateRequest(data, collection._id);

    onCancel();
  }, [name, isPrivate, type, description, add_tags, remove_tags, collection, onCancel]);

  //----------------------------------------------------------------

  const handleDelete = useCallback(async (collectionId) => {
    setWorking(true);
    const deleteResult = deleteUserCollection(user, collectionId);
    setWorking(false);

    if (deleteResult){
      navigate("/dashboard/collections");
    } else {
      setToasts([...toasts, createToast(
        `Failed to delete collection`
      )]);
    }
  },[toasts]);

  //----------------------------------------------------------------

  const updateTags = (data) => {
    setAddTags(data['add_tags']);
    setRemoveTags(data['remove_tags']);
  };

  //----------------------------------------------------------------

  return(
    <Form>

      <Form.Group className="mb-4">
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={working}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          value={description}
          placeholder="Enter descrition"
          onChange={(event) => setDescription(event.target.value)}
          disabled={working}
        />
      </Form.Group>

      <UpdateFormTags
        object={collection}
        onChange={updateTags}
        disabled={working}
      />

      {/* TYPE */}
      <Form.Group className="mb-4">
        <Form.Label>Collection Type</Form.Label>
        <Form.Select
          onChange={(event) => setType(event.target.value)}
          disabled={working}
          defaultValue={type}
        >
          {Object.keys(CollectionTypes).map(key =>
            <option
              key={`itemType-option-${key}`}
              value={CollectionTypes[key]}
            >
              {CollectionTypes[key]}
            </option>
          )}
        </Form.Select>
      </Form.Group>

      {/* PRIVATE */}
      <Form.Group className="mb-4">
        <Form.Check
          type='checkbox'
          defaultChecked={isPrivate}
          onClick={(event) => setIsPrivate(event.target.checked)}
          label='Private'
        />
      </Form.Group>

      <Row className="align-items-center mb-4">
        <Col className="text-start">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={working}
          >
            Cancel
          </Button>
        </Col>
        <Col className="text-end">
          <Button
            className="text-nowrap"
            variant="primary"
            onClick={submitHandler}
            disabled={working}
          >
            {working ?
              <BasicSpinner text="Saving Changes..." />
              :
              "Save Changes"
            }
          </Button>
        </Col>
      </Row>

      <Row>
        <Col className="text-end">
          <span className="me-2">
            Permanently delete
          </span>
          <RemoveButton
            objectId={collection._id}
            id={`row-${collection._id}`}
            onClick={handleDelete}
            buttonIcon="trash"
          />
        </Col>
      </Row>

      <Toaster toasts={toasts} />

    </Form>
  );
}
